/** The chip row: All … Settings, the Notifications count, and the project filter at the right end. */
import { useMemo } from "react";
import {
  Button,
  ChipGroup,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icons,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { HUB_CHIPS, type HubChip } from "@/lib/global-search";
import { useProjectStore } from "@/stores/project-store";
import { PROJECT_SCOPED_CHIPS } from "./hub-sections";

const HUB_CHIP_LABEL_KEYS = {
  all: "chipAll",
  chats: "chats",
  notifications: "notifications",
  tasks: "tasks",
  frames: "frames",
  memory: "memory",
  settings: "settings",
} as const satisfies Record<HubChip, string>;

export function HubChipRow({
  chip,
  onChipChange,
  notificationCount,
  projectId,
  onProjectChange,
}: {
  chip: HubChip;
  onChipChange: (chip: HubChip) => void;
  notificationCount: number;
  projectId: string | null;
  onProjectChange: (projectId: string | null) => void;
}) {
  const t = useT(panelsSearchMessages);
  const projects = useProjectStore((state) => state.projects);
  const activeProjects = projects.filter(
    (project) => project.status === "active",
  );
  const selected =
    activeProjects.find((project) => project.id === projectId) ?? null;
  const scoped = PROJECT_SCOPED_CHIPS.has(chip);
  const options = useMemo(
    () =>
      HUB_CHIPS.map((value) => ({
        value,
        label: t(HUB_CHIP_LABEL_KEYS[value]),
        count:
          value === "notifications" && notificationCount > 0
            ? notificationCount
            : undefined,
        testId: `hub-chip-${value}`,
      })),
    [notificationCount, t],
  );
  return (
    <div
      className="flex flex-none items-center gap-0.5 py-2.5"
      data-testid="hub-chip-row"
    >
      <ChipGroup
        value={chip}
        onChange={onChipChange}
        options={options}
        variant="chips"
        ariaLabel={t("showResultsFor")}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            // Hidden keeps its space so the chips never move (mockup `.scope.off`); invisible is not focusable.
            className={cn("ml-auto gap-1 px-2.5", !scoped && "invisible")}
            data-testid="hub-project-filter"
          >
            <span className="max-w-[160px] truncate">
              {selected?.name ?? t("allProjects")}
            </span>
            <Icons.ChevronDown className="h-3 w-3" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          data-tray-popover="true"
          data-testid="hub-project-filter-menu"
        >
          <DropdownMenuItem
            onSelect={() => onProjectChange(null)}
            trailing={projectId === null ? <Icons.Check /> : undefined}
            data-testid="hub-project-filter-all"
          >
            {t("allProjects")}
          </DropdownMenuItem>
          {activeProjects.map((project) => (
            <DropdownMenuItem
              key={project.id}
              onSelect={() => onProjectChange(project.id)}
              trailing={project.id === projectId ? <Icons.Check /> : undefined}
              data-testid={`hub-project-filter-${project.id}`}
            >
              {project.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
